/**
 * Наложение личного черновика на опубликованные данные.
 *
 * Сервер отдаёт два слоя: опубликованные бренды (их видят все) и плоский список
 * правок текущего пользователя. Интерфейс показывает значение из черновика,
 * помечая ячейку как черновую, и всегда знает опубликованное значение — чтобы
 * показать «было → стало» и дать отменить одну правку.
 */
import type { Brand, BrandCategory, Change, ChannelCode } from '@shared/types.ts'

export const keys = {
  brandField: (brandId: number, field: string) => `brand:${brandId}:${field}`,
  channelField: (brandId: number, channel: ChannelCode, field: string) => `channel:${brandId}:${channel}:${field}`,
  categoryChannelField: (brandCategoryId: number, channel: ChannelCode, field: string) =>
    `catchan:${brandCategoryId}:${channel}:${field}`,
  customField: (entityType: 'brand' | 'brand_channel' | 'brand_category', entityId: number, fieldId: number) =>
    `field:${entityType}:${entityId}:${fieldId}`,
}

export interface Overlay {
  /** правки, адресуемые по ячейке */
  byKey: Map<string, Change>
  /** привязки категорий, помеченные к отвязке */
  detached: Set<number>
  /** созданные в черновике и ещё не опубликованные */
  draftBrands: Set<number>
  draftCategories: Set<number>
  /** бренды, у которых есть хоть одна правка — для подсветки строки */
  touchedBrands: Set<number>
  draftComments: number
  /** Черновые комментарии по брендам: их показывает колонка «Комментарий». */
  commentsByBrand: Map<number, Change[]>
  count: number
}

export const emptyOverlay = (): Overlay => ({
  byKey: new Map(),
  detached: new Set(),
  draftBrands: new Set(),
  draftCategories: new Set(),
  touchedBrands: new Set(),
  draftComments: 0,
  commentsByBrand: new Map(),
  count: 0,
})

export function buildOverlay(changes: Change[]): Overlay {
  const overlay = emptyOverlay()
  overlay.count = changes.length

  for (const change of changes) {
    if (change.brandId) overlay.touchedBrands.add(change.brandId)

    if (change.action === 'detach' && change.entityId) {
      overlay.detached.add(change.entityId)
      continue
    }
    if (change.action === 'create' && change.entityType === 'brand' && change.entityId) {
      overlay.draftBrands.add(change.entityId)
      continue
    }
    if ((change.action === 'create' || change.action === 'attach') && change.entityType === 'brand_category' && change.entityId) {
      overlay.draftCategories.add(change.entityId)
      continue
    }
    if (change.action === 'comment') {
      overlay.draftComments += 1
      if (change.brandId) {
        const list = overlay.commentsByBrand.get(change.brandId) ?? []
        list.push(change)
        overlay.commentsByBrand.set(change.brandId, list)
      }
      continue
    }
    if (change.action !== 'update' || !change.fieldCode) continue

    const { key, fieldCode } = change
    if (change.entityType === 'brand' && change.entityId) {
      overlay.byKey.set(keys.brandField(change.entityId, fieldCode), change)
    } else if (change.entityType === 'brand_channel' && key.brandId && key.channelCode) {
      overlay.byKey.set(keys.channelField(key.brandId, key.channelCode, fieldCode), change)
    } else if (change.entityType === 'brand_category_channel' && key.brandCategoryId && key.channelCode) {
      overlay.byKey.set(keys.categoryChannelField(key.brandCategoryId, key.channelCode, fieldCode), change)
    } else if (change.entityType === 'field_value' && key.fieldId) {
      /* Ключ ячейки строится по владельцу значения (бренд или привязка
         категории), а не по id строки в field_values: ячейка о нём не знает и
         ищет правку по своему бренду. С entityId правка в таблице не
         показывалась — значение возвращалось к прежнему до подтверждения. */
      const scope = key.channelCode ? 'brand_channel' : key.brandCategoryId ? 'brand_category' : 'brand'
      const owner = key.brandCategoryId ?? key.brandId
      if (owner) overlay.byKey.set(keys.customField(scope, owner, key.fieldId), change)
    }
  }
  return overlay
}

export interface Cell<T = unknown> {
  /** что показываем */
  value: T
  /** что опубликовано */
  published: T
  /** значение изменено в черновике */
  isDraft: boolean
  /** подпись черновичного значения, если сервер её прислал */
  label: string | null
  change: Change | null
}

function cell<T>(published: T, change: Change | undefined): Cell<T> {
  if (!change) return { value: published, published, isDraft: false, label: null, change: null }
  return {
    value: change.newValue as T,
    published,
    isDraft: true,
    label: change.newLabel,
    change,
  }
}

/** Значение поля бренда с учётом черновика. */
export function brandCell<T = unknown>(overlay: Overlay, brand: Brand, field: 'name' | 'categoriesUnclear'): Cell<T> {
  return cell(brand[field] as T, overlay.byKey.get(keys.brandField(brand.id, field)))
}

/** Значение поля канала с учётом черновика. Канала может не быть вовсе. */
export function channelCell<T = unknown>(
  overlay: Overlay, brand: Brand, channel: ChannelCode, field: string,
): Cell<T | null> {
  const values = brand.channels[channel]
  const published = (values ? (values as unknown as Record<string, unknown>)[field] : null) as T | null
  return cell(published ?? null, overlay.byKey.get(keys.channelField(brand.id, channel, field)))
}

/** Значение поля категории бренда в канале. */
export function categoryCell<T = unknown>(
  overlay: Overlay, category: BrandCategory, channel: ChannelCode, field: 'isActive' | 'tier',
): Cell<T | null> {
  const values = category.channels[channel]
  const published = (values ? (values as unknown as Record<string, unknown>)[field] : null) as T | null
  return cell(published ?? null, overlay.byKey.get(keys.categoryChannelField(category.id, channel, field)))
}

/** Значение пользовательского поля. */
export function customFieldCell<T = unknown>(
  overlay: Overlay,
  scope: 'brand' | 'brand_channel' | 'brand_category',
  entityId: number,
  fieldId: number,
  fieldCode: string,
  source: Record<string, unknown>,
): Cell<T | null> {
  const published = (source[fieldCode] ?? null) as T | null
  return cell(published, overlay.byKey.get(keys.customField(scope, entityId, fieldId)))
}

/** Есть ли у бренда хоть одна правка в черновике — для подсветки строки и фильтра «мои правки». */
export function brandHasDraft(overlay: Overlay, brand: Brand): boolean {
  if (overlay.draftBrands.has(brand.id)) return true
  if (overlay.touchedBrands.has(brand.id)) return true
  return brand.categories.some((c) => overlay.detached.has(c.id) || overlay.draftCategories.has(c.id))
}
